/**
 * BOE-40 — the Boer landing's contact form.
 *
 * "Talk to us about your packhouse", the pricing card's "Talk to us", the
 * footer "Contact" and (BOE-41) the "Under investigation" buttons all open this
 * sheet instead of a dead `#contact` link. Plain DOM + CSS strings injected by
 * buildBoerLanding.ts — the landing has a size budget and runs as a sandboxed
 * srcdoc frame, so no framework.
 *
 * The frame is sandboxed WITHOUT `allow-same-origin` (opaque origin), so it does
 * not call the API itself: it posts the validated form to the shell
 * (`type: "contact"`), and CustomLanding.vue answers with `type:
 * "contact-result"` after POST /website/landing-contact. No answer within
 * CONTACT_TIMEOUT_MS (a preview with no shell, a dead network) is an error, and
 * every error offers a mailto to the address the form names.
 */

/** Shown as the recipient, used for the mailto fallback. The backend routes by the same address. */
export const BOER_SUPPORT_EMAIL = "support@theboer.co.za";

/** Which button opened the form — mirrors LANDING_CONTACT_CONTEXTS in metaship-backend Services/Website. */
export const BOER_CONTACT_CONTEXTS = ["packhouse", "pricing", "contact", "start-free"] as const;
export type BoerContactContext = (typeof BOER_CONTACT_CONTEXTS)[number];

/** Field caps — the same limits the endpoint enforces (LANDING_CONTACT_MAX). */
export const BOER_CONTACT_MAX = { name: 120, email: 254, phone: 40, farm: 160, message: 4000 } as const;

const CONTACT_TIMEOUT_MS = 20000;

export const CONTACT_CSS = `
  .contact[hidden] { display: none; }
  .contact { position: fixed; inset: 0; z-index: 60; display: flex; align-items: center; justify-content: center; padding: 24px; }
  .contact__scrim { position: absolute; inset: 0; background: rgba(8,28,25,.55); }
  .contact__panel { position: relative; width: min(520px, 100%); max-height: calc(100dvh - 48px); overflow-y: auto; overscroll-behavior: contain; background: var(--paper); color: var(--ink); border-radius: 22px; box-shadow: 0 24px 64px -16px rgba(8,28,25,.45); padding: 28px 28px 24px; }
  .contact__close { position: absolute; top: 14px; right: 14px; width: 44px; height: 44px; display: inline-flex; align-items: center; justify-content: center; border: 0; border-radius: 999px; background: transparent; color: var(--ink-soft); cursor: pointer; }
  .contact__close:hover { background: var(--salt-deep); }
  .contact__title { font-size: 1.6rem; padding-right: 44px; }
  .contact__intro { margin-top: 8px; color: var(--ink-soft); font-size: .98rem; }
  .contact__to { margin-top: 14px; display: flex; flex-wrap: wrap; gap: 6px 10px; align-items: baseline; font-size: .92rem; color: var(--ink-mute); }
  .contact__to b { font-family: var(--mono); font-size: .86rem; font-weight: 500; color: var(--kelp); overflow-wrap: anywhere; }
  .contact__form { display: grid; gap: 14px; margin-top: 18px; }
  .contact__field { display: grid; gap: 6px; min-width: 0; }
  .contact__field label { font-size: .88rem; font-weight: 700; }
  .contact__field input, .contact__field textarea { width: 100%; min-width: 0; font: inherit; font-size: 16px; color: var(--ink); background: var(--salt); border: 1.5px solid var(--dune); border-radius: 12px; padding: 11px 13px; }
  .contact__field textarea { min-height: 110px; resize: vertical; }
  .contact__field input:focus, .contact__field textarea:focus { outline: none; border-color: var(--kelp); background: var(--paper); }
  .contact__field [aria-invalid="true"], .contact__field [aria-invalid="true"]:focus { border-color: #B3261E; }
  .contact__err { color: #B3261E; font-size: .84rem; }
  .contact__err[hidden] { display: none; }
  .contact__hp { position: absolute; left: -10000px; width: 1px; height: 1px; overflow: hidden; }
  .contact__actions { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; margin-top: 4px; }
  .contact__actions .btn { justify-content: center; }
  .contact__actions .btn[disabled] { opacity: .65; cursor: progress; transform: none; }
  .contact__status { display: none; margin-top: 18px; }
  .contact__status p { color: var(--ink-soft); }
  .contact__status a { color: var(--kelp); font-weight: 700; overflow-wrap: anywhere; }
  .contact[data-state="success"] .contact__form, .contact[data-state="success"] .contact__intro { display: none; }
  .contact[data-state="success"] [data-contact-done] { display: grid; gap: 14px; }
  .contact[data-state="error"] [data-contact-failed] { display: grid; gap: 8px; padding: 12px 14px; border-radius: 12px; background: #FCEDEC; }
  html.contact-open, html.contact-open body { overflow: hidden; }
  /* Phone: a bottom sheet with full-width controls and room for the keyboard. */
  @media (max-width: 560px) {
    .contact { align-items: flex-end; padding: 0; }
    .contact__panel { width: 100%; max-height: 92dvh; border-radius: 22px 22px 0 0; padding: 22px 16px calc(18px + env(safe-area-inset-bottom)); }
    .contact__title { font-size: 1.35rem; }
    .contact__actions .btn { flex: 1 1 100%; }
  }
`;

/** One labelled input/textarea with its error line. English defaults; the runtime translates. */
function field(name: string, labelKey: string, label: string, control: string): string {
  return `<div class="contact__field">
          <label for="boer-contact-${name}" data-i18n="${labelKey}">${label}</label>
          ${control}
          <p class="contact__err" id="boer-contact-${name}-err" data-contact-err="${name}" role="alert" hidden></p>
        </div>`;
}

export const CONTACT_MARKUP = `
<div class="contact" data-contact data-state="form" hidden>
  <div class="contact__scrim" data-contact-close></div>
  <div class="contact__panel" role="dialog" aria-modal="true" aria-labelledby="boer-contact-title">
    <button type="button" class="contact__close" data-contact-close aria-label="Close" data-i18n-aria="contact.close">
      <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true"><path d="M5 5l10 10M15 5L5 15" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"/></svg>
    </button>
    <h3 class="contact__title" id="boer-contact-title" data-contact-title data-i18n="contact.title.contact">Contact us</h3>
    <p class="contact__intro" data-contact-intro data-i18n="contact.intro">Send us a note and we'll get back to you.</p>
    <p class="contact__to"><span data-i18n="contact.to">Sends to</span> <b data-contact-recipient>${BOER_SUPPORT_EMAIL}</b></p>
    <form class="contact__form" data-contact-form novalidate>
      <input type="hidden" name="context" value="contact">
      ${field("name", "contact.name", "Your name", `<input id="boer-contact-name" name="name" type="text" autocomplete="name" maxlength="${BOER_CONTACT_MAX.name}" required aria-describedby="boer-contact-name-err">`)}
      ${field("email", "contact.email", "E-mail", `<input id="boer-contact-email" name="email" type="email" autocomplete="email" inputmode="email" maxlength="${BOER_CONTACT_MAX.email}" required aria-describedby="boer-contact-email-err">`)}
      ${field("phone", "contact.phone", "Phone (optional)", `<input id="boer-contact-phone" name="phone" type="tel" autocomplete="tel" inputmode="tel" maxlength="${BOER_CONTACT_MAX.phone}" aria-describedby="boer-contact-phone-err">`)}
      ${field("farm", "contact.farm", "Farm or packhouse (optional)", `<input id="boer-contact-farm" name="farm" type="text" autocomplete="organization" maxlength="${BOER_CONTACT_MAX.farm}" aria-describedby="boer-contact-farm-err">`)}
      ${field("message", "contact.message", "Message", `<textarea id="boer-contact-message" name="message" maxlength="${BOER_CONTACT_MAX.message}" required aria-describedby="boer-contact-message-err"></textarea>`)}
      <div class="contact__hp" aria-hidden="true"><label>Website <input name="website" type="text" tabindex="-1" autocomplete="off"></label></div>
      <div class="contact__status" data-contact-failed role="alert">
        <p data-contact-error-text></p>
        <a data-contact-mailto href="mailto:${BOER_SUPPORT_EMAIL}" target="_blank" rel="noopener"><span data-i18n="contact.errorMail">E-mail us instead:</span> ${BOER_SUPPORT_EMAIL}</a>
      </div>
      <div class="contact__actions">
        <button type="submit" class="btn btn-primary" data-contact-send data-i18n="contact.send">Send</button>
      </div>
    </form>
    <div class="contact__status" data-contact-done role="status">
      <p data-contact-success></p>
      <div class="contact__actions"><button type="button" class="btn btn-ghost" data-contact-close data-i18n="contact.done">Close</button></div>
    </div>
  </div>
</div>`;

/**
 * Runtime, spliced INTO boerLandingRuntime's IIFE (it uses that scope's COPY,
 * `locale` and setMenu). Returns the hooks the IIFE wires: openContact,
 * closeContact, onContactKey, onContactMessage, startContact.
 */
export const CONTACT_RUNTIME = `
  var SUPPORT_EMAIL = ${JSON.stringify(BOER_SUPPORT_EMAIL)};
  var CONTACT_CONTEXTS = ${JSON.stringify(BOER_CONTACT_CONTEXTS)};
  var CONTACT_MAX = ${JSON.stringify(BOER_CONTACT_MAX)};
  var CONTACT_TIMEOUT_MS = ${CONTACT_TIMEOUT_MS};
  var EMAIL_RE = /^[^\\s@]+@[^\\s@]+\\.[^\\s@]{2,}$/;
  var PHONE_RE = /^[+()\\d\\s.\\/-]+$/;
  var contactSeq = 0;
  var contactPending = null;
  var contactTimer = null;
  var contactReturnFocus = null;

  function tr(key) {
    return (COPY[locale] && COPY[locale][key]) || (COPY.en && COPY.en[key]) || "";
  }
  function contactBox() { return document.querySelector("[data-contact]"); }
  function contactForm() { return document.querySelector("[data-contact-form]"); }

  function setContactState(state) {
    var box = contactBox();
    if (!box) return;
    box.setAttribute("data-state", state);
    var send = box.querySelector("[data-contact-send]");
    if (send) {
      send.disabled = state === "sending";
      var key = state === "sending" ? "contact.sending" : "contact.send";
      send.setAttribute("data-i18n", key);
      send.textContent = tr(key);
    }
  }

  function setI18n(el, key) {
    if (!el) return;
    el.setAttribute("data-i18n", key);
    el.textContent = tr(key);
  }

  function clearContactErrors(form) {
    var errs = form.querySelectorAll("[data-contact-err]");
    for (var i = 0; i < errs.length; i++) {
      errs[i].hidden = true;
      errs[i].textContent = "";
      var input = form.elements[errs[i].getAttribute("data-contact-err")];
      if (input) input.removeAttribute("aria-invalid");
    }
  }

  function openContact(context) {
    var box = contactBox();
    var form = contactForm();
    if (!box || !form) return false;
    if (CONTACT_CONTEXTS.indexOf(context) < 0) context = "contact";
    contactReturnFocus = document.activeElement;
    form.elements.context.value = context;
    var startFree = context === "start-free";
    setI18n(box.querySelector("[data-contact-title]"), "contact.title." + (startFree ? "startFree" : context));
    setI18n(box.querySelector("[data-contact-intro]"), startFree ? "contact.introStartFree" : "contact.intro");
    if (startFree && !form.elements.message.value) form.elements.message.value = tr("contact.messageStartFree");
    clearContactErrors(form);
    setContactState("form");
    box.hidden = false;
    document.documentElement.classList.add("contact-open");
    setTimeout(function () { if (form.elements.name) form.elements.name.focus(); }, 0);
    return true;
  }

  function closeContact() {
    var box = contactBox();
    if (!box || box.hidden) return;
    box.hidden = true;
    document.documentElement.classList.remove("contact-open");
    if (box.getAttribute("data-state") === "success") setContactState("form");
    if (contactReturnFocus && contactReturnFocus.focus) contactReturnFocus.focus();
    contactReturnFocus = null;
  }

  /** Same rules as the endpoint, so the visitor hears about a bad field before a round trip. */
  function validateContactForm(form) {
    var v = function (name) { var el = form.elements[name]; return el ? String(el.value || "").trim() : ""; };
    var payload = {
      name: v("name"), email: v("email").toLowerCase(), phone: v("phone"), farm: v("farm"),
      message: v("message"), context: v("context"), locale: locale, website: v("website"),
    };
    var errors = {};
    if (!payload.name) errors.name = "contact.required";
    if (!payload.email) errors.email = "contact.required";
    else if (!EMAIL_RE.test(payload.email) || payload.email.length > CONTACT_MAX.email) errors.email = "contact.emailInvalid";
    if (payload.phone && (!PHONE_RE.test(payload.phone) || payload.phone.replace(/\\D/g, "").length < 6)) errors.phone = "contact.phoneInvalid";
    if (!payload.message) errors.message = "contact.required";
    for (var k in CONTACT_MAX) {
      if (payload[k] && payload[k].length > CONTACT_MAX[k] && !errors[k]) errors[k] = "contact.tooLong";
    }
    return { ok: Object.keys(errors).length === 0, payload: payload, errors: errors };
  }

  function showContactErrors(form, errors) {
    var first = null;
    for (var name in errors) {
      var err = form.querySelector('[data-contact-err="' + name + '"]');
      var input = form.elements[name];
      if (err) { err.textContent = tr(errors[name]); err.hidden = false; }
      if (input) { input.setAttribute("aria-invalid", "true"); if (!first) first = input; }
    }
    if (first) first.focus();
  }

  function mailtoHref(payload) {
    var body = payload.message ? payload.message.slice(0, 1500) : "";
    return "mailto:" + SUPPORT_EMAIL + "?subject=" + encodeURIComponent(tr("contact.mailSubject")) + (body ? "&body=" + encodeURIComponent(body) : "");
  }

  function finishContact(id, ok, rateLimited) {
    if (!contactPending || contactPending.id !== id) return;
    clearTimeout(contactTimer);
    var pending = contactPending;
    contactPending = null;
    var box = contactBox();
    var form = contactForm();
    if (!box || !form) return;
    if (ok) {
      var done = box.querySelector("[data-contact-success]");
      if (done) done.textContent = tr("contact.success").replace("{email}", pending.payload.email);
      form.reset();
      setContactState("success");
      var closeBtn = box.querySelector("[data-contact-done] [data-contact-close]");
      if (closeBtn) closeBtn.focus();
      return;
    }
    var text = box.querySelector("[data-contact-error-text]");
    if (text) text.textContent = tr(rateLimited ? "contact.rateLimited" : "contact.error");
    var mail = box.querySelector("[data-contact-mailto]");
    if (mail) mail.setAttribute("href", mailtoHref(pending.payload));
    setContactState("error");
  }

  function onContactSubmit(event) {
    event.preventDefault();
    var form = event.currentTarget || contactForm();
    if (contactPending) return;
    clearContactErrors(form);
    var result = validateContactForm(form);
    if (!result.ok) { showContactErrors(form, result.errors); return; }
    var id = "contact-" + (++contactSeq);
    contactPending = { id: id, payload: result.payload };
    setContactState("sending");
    contactTimer = setTimeout(function () { finishContact(id, false, false); }, CONTACT_TIMEOUT_MS);
    try {
      parent.postMessage({ source: "metaship-landing", type: "contact", id: id, payload: result.payload }, "*");
    } catch (e) {
      finishContact(id, false, false);
    }
  }

  /** The shell's answer to a "contact" message. */
  function onContactMessage(data) {
    if (!data || data.type !== "contact-result" || typeof data.id !== "string") return;
    finishContact(data.id, data.ok === true, data.status === 429);
  }

  /** Keep Tab inside the open sheet; Escape is handled by the page's own key handler. */
  function onContactKey(event) {
    var box = contactBox();
    if (!box || box.hidden || event.key !== "Tab") return;
    var nodes = box.querySelectorAll("button, input:not([type=hidden]):not([tabindex='-1']), textarea, a[href]");
    var list = [];
    for (var i = 0; i < nodes.length; i++) if (nodes[i].offsetParent !== null || nodes[i] === document.activeElement) list.push(nodes[i]);
    if (!list.length) return;
    var first = list[0], last = list[list.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }

  function startContact() {
    var form = contactForm();
    if (form) form.addEventListener("submit", onContactSubmit);
    var closers = document.querySelectorAll("[data-contact-close]");
    for (var i = 0; i < closers.length; i++) {
      closers[i].addEventListener("click", function (event) { event.preventDefault(); closeContact(); });
    }
    var panel = document.querySelector(".contact__panel");
    // Clicks inside the sheet must not reach the page's "click outside closes menus" handler.
    if (panel) panel.addEventListener("click", function (event) { event.stopPropagation(); });
    document.addEventListener("keydown", onContactKey);
  }
`;
